import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, Linkedin, Instagram, Facebook } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { publicSupabase } from "@/integrations/supabase/publicClient";
import logo from "@/assets/logo.webp";
import { TrustindexWidget } from "@/components/TrustindexWidget";
import { TrustooWidget } from "@/components/TrustooWidget";
import { KvwbKeurmerkWidget } from "@/components/KvwbKeurmerkWidget";


function FooterReviewWidget() {
  return <TrustindexWidget loaderId="3d274356493a404c8c16690f171" />;
}


function FooterSeoLinks() {
  const { data: seoPages } = useQuery({
    queryKey: ["footer-seo-pages"],
    queryFn: async () => {
      const { data, error } = await publicSupabase
        .from("seo_pages" as any)
        .select("slug, plaatsnaam")
        .eq("status", "gepubliceerd")
        .eq("show_in_footer", true)
        .order("plaatsnaam", { ascending: true });
      if (error) throw error;
      return data as any[] || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  if (!seoPages || seoPages.length === 0) return null;

  return (
    <div className="mt-12 pt-8 border-t border-background/10">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-background">Werkgebieden</h3>
        <Link to="/werkgebieden" className="text-xs text-background/50 hover:text-background transition-colors">
          Alle werkgebieden →
        </Link>
      </div>
      <div className="columns-2 md:columns-4 gap-x-8">
        {seoPages.map((page: any) => (
          <Link
            key={page.slug}
            to={`/${page.slug}`}
            className="block text-sm text-background/60 hover:text-background transition-colors truncate mb-2"
          >
            {page.plaatsnaam}
          </Link>
        ))}
      </div>
    </div>
  );
}

function WhatsAppIcon({ className }: {className?: string;}) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.118 1.532 5.845L.057 23.571a.5.5 0 0 0 .612.612l5.726-1.475A11.942 11.942 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.808 9.808 0 0 1-5.031-1.383l-.361-.214-3.737.962.992-3.625-.236-.374A9.808 9.808 0 0 1 2.182 12C2.182 6.57 6.57 2.182 12 2.182S21.818 6.57 21.818 12 17.43 21.818 12 21.818z" />
    </svg>);

}

const socialLinks = [
{ name: "LinkedIn", href: "https://www.linkedin.com/company/online-sale-specialist/", icon: Linkedin },
{ name: "Instagram", href: "https://www.instagram.com/onlinesalespecialist", icon: Instagram },
{ name: "Facebook", href: "https://www.facebook.com/onlinesalespecialist", icon: Facebook },
{ name: "WhatsApp", href: "https://wa.me/31592887788", icon: WhatsAppIcon }];


const services = [
{ name: "Website bouwen", href: "/diensten/websites-bouwen" },
{ name: "Website per maand", href: "/diensten/website-per-maand" },
{ name: "Webshop bouwen", href: "/diensten/webshops-bouwen" },
{ name: "SEO", href: "/diensten/seo" },
{ name: "SEA", href: "/diensten/sea" },
{ name: "Hosting", href: "/diensten/hosting" },
{ name: "Website onderhoud", href: "/diensten/website-onderhoud" }];


const quickLinks = [
{ name: "Over Mij", href: "/over-mij" },
{ name: "Projecten", href: "/projecten" },
{ name: "Blog", href: "/blog" },
{ name: "Reviews", href: "/reviews" },
{ name: "Onderwerpen", href: "/onderwerpen" },
{ name: "Contact", href: "/contact" },
{ name: "Offerte Aanvragen", href: "/offerte-aanvragen" }];


export function Footer() {
  const currentYear = new Date().getFullYear();


  return (
    <footer className="gradient-bg text-white">
      <div className="container-wide section-padding-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="space-y-6">
            <img
              src={logo}
              alt="Online Sale Specialist"
              className="h-8 w-auto brightness-0 invert"
              width={177}
              height={32}
              loading="lazy"
              decoding="async" />
            
            <p className="text-background/70 text-sm leading-relaxed">
              Jouw vaste aanspreekpunt voor alles rondom online aanwezigheid. Persoonlijk, direct en resultaatgericht.
            </p>
            <div className="space-y-3">
              <a href="tel:+31592887788" className="flex items-center gap-3 text-sm text-background/70 hover:text-background transition-colors">
                <Phone className="h-4 w-4" />
                <span>0592 88 77 88</span>
              </a>
              <a href="mailto:info@onlinesalespecialist.nl" className="flex items-center gap-3 text-sm text-background/70 hover:text-background transition-colors">
                <Mail className="h-4 w-4" />
                <span>info@onlinesalespecialist.nl</span>
              </a>
              <div className="flex items-center gap-3 text-sm text-background/70">
                <MapPin className="h-4 w-4 shrink-0" />
                <span>Assen</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-background/70">
                <Clock className="h-4 w-4 shrink-0" />
                <span>Ma - Vr: 09:00 – 17:00</span>
              </div>
            </div>
            
            {/* Social Media */}
            <div className="pt-4">
              <h3 className="text-sm font-semibold mb-3 text-background">Volg mij</h3>
              <div className="flex items-center gap-3">
                {socialLinks.map((social) =>
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-background/10 hover:bg-background/20 text-background/70 hover:text-background transition-colors"
                  aria-label={social.name}>
                  
                    <social.icon className="h-4 w-4" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Snelle Links - moved to second column */}
          <nav aria-label="Snelle links">
            <h3 className="text-sm font-semibold mb-6 text-background">Snelle Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) =>
              <li key={link.href}>
                  <Link to={link.href} className="text-sm text-background/70 hover:text-background transition-colors">
                    {link.name}
                  </Link>
                </li>
              )}
            </ul>
          </nav>

          {/* Diensten - now third column with reduced list */}
          <nav aria-label="Diensten">
            <h3 className="text-sm font-semibold mb-6 text-background">Diensten</h3>
            <ul className="space-y-3">
              {services.map((service) =>
              <li key={service.href}>
                  <Link to={service.href} className="text-sm text-background/70 hover:text-background transition-colors">
                    {service.name}
                  </Link>
                </li>
              )}
            </ul>
          </nav>

          {/* Trustindex Reviews Widget */}
          <div>
            <h3 className="text-sm font-semibold mb-6 text-background">Klantbeoordelingen</h3>
            <FooterReviewWidget />

            <div className="mt-6">
              <TrustooWidget widgetId="FRQXfbjwO0usxUS5FbSMbBA4cAT6qTk3gE0yGPBovQz4Og" />
            </div>

            <h3 className="text-sm font-semibold mt-10 mb-6 text-background">Keurmerk</h3>
            <KvwbKeurmerkWidget keurmerkId="1712" variant="compact" theme="dark" />
          </div>

        </div>

        {/* SEO Pages Links */}
        <FooterSeoLinks />

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-background/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-xs text-background/50 text-center md:text-left">
              <p className="text-sm">© {new Date().getFullYear()} Online Sale Specialist. Alle rechten voorbehouden.</p>
            </div>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-sm text-background/50 hover:text-background transition-colors">
                Privacybeleid
              </Link>
              <Link to="/voorwaarden" className="text-sm text-background/50 hover:text-background transition-colors">
                Algemene Voorwaarden
              </Link>
              <a
                href="/sitemap.xml"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-background/50 hover:text-background transition-colors">
                
                Sitemap
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>);

}